<template>
    <section :debugid="'premedia-'+this.id" :class="['job-premedia__premedia-group', {editing:premediaRowIsEditing}]">

        <!-- Swap Toolbar -->
        <div class="premedia-group__toolbar grid-a">
            <div class="toolbar__num">
                <div class="headline">{{index+1}}</div>
            </div>
            <div class="toolbar__select">
                <v-radio-group
                        v-if="premediaRowIsEditing"
                        app dark
                        row mandatory
                        class="select__premedia-type"
                        v-model="premediaRowType">
                    <v-radio
                            v-for="item in options.premedia_row_types"
                            :key="`${generateUniqueHash(item)}`"
                            color="white"
                            :label="item.name"
                            :value="item.value"/>
                </v-radio-group>
                <div
                        v-else
                        class="display__premedia-type">
                    <p>{{getRowTypeLabel}}</p>
                </div>
            </div>
        </div>


        <!-- Swap Content -->
        <div class="premedia-group__content grid-b">


            <!-- PRODUCTS COLUMN -->
            <div class="content__shoe grid-d">
                <h3 class="section-title body-1"><span v-if="premediaRowIsEditing">Add </span>Products</h3>
                <v-autocomplete
                        v-if="premediaRowIsEditing"
                        solo dense
                        hide-selected
                        return-object
                        color="primary"
                        item-text="label"
                        item-value="label"
                        placeholder="Search by Style ID and Color"
                        v-model="productsModel"
                        :hide-no-data="!hasTermData"
                        :items="productsData"
                        :loading="productsLoading"
                        :search-input.sync="productsTerm"
                        @blur="clearProductData">

                    <template
                            slot="item"
                            slot-scope="productsData">
                        <autocomplete-item
                                :image-src="productsData.item.thumbSrc"
                                :label="productsData.item.label"/>
                    </template>
                </v-autocomplete>

                <div
                        v-if="products.length"
                        class="premedia-items"
                        ref="swapShoes">

                    <!--
                    // index: index in products array
                        // items-index: index in row.items array | used for getting ID of item to update or delete
                    // new-item-index: index of new items in state.premediaRow_ItemsToCreate | used for tying v-model of note (and others down the road) to the data of only newly added items (items that arent on the server yet)
                     -->
                    <premedia-item
                            v-for="(item,index) in products"
                            cardtype="product"
                            :key="generateUniqueHash(item)"
                            :id="item.id"
                            :temp-id="item.temp_id"
                            :row-index="rowIndex"
                            :row-id="id"
                            :title=parseItemMeta(item.metadata).label
                            :type="item.type"
                            :item="item"
                            :note="item.note"
                            :approved="item.is_approved"
                            :img="item.url"
                            :isEditing="premediaRowIsEditing"
                            :can-approve="canApprove"
                            :can-view-approved="canViewApproved"
                    />

                </div>
                <div v-else><p>No products attached</p></div>
            </div>




            <!-- ASSETS COLUMN -->
            <div class="content__images grid-e">
                <h3 class="section-title body-1"><span v-if="premediaRowIsEditing">Add </span>Assets</h3>
                <div v-if="premediaRowIsEditing" class="upload-assets-copy">
                    <core-uploader
                            v-if="this.id !== null"
                            multiple
                            ref="coreUploader"
                            class="core-uploader"
                            :key="`${generateUniqueHash(this.id)}`"
                            :attachTo="attachTo"
                            :fileUsageType="fileUsageType"/>
                    <add-item-button
                            size="large"
                            label="Or upload file"
                            @click.native="uploadPremediaAttachment"/>
                </div>
                <div class="add-assets-dropdown">
                    <v-autocomplete
                            v-if="premediaRowIsEditing"
                            solo dense
                            hide-selected
                            return-object
                            color="primary"
                            item-text="filename"
                            item-value="filename"
                            placeholder="Search by Mrktng image or Print Ad filename"
                            v-model="assetsModel"
                            :hide-no-data="!hasTermData"
                            :items="assetsData"
                            :loading="assetsLoading"
                            :search-input.sync="assetsTerm"
                            @blur="clearAssetData">

                        <template
                                slot="item"
                                slot-scope="assetsData">
                            <autocomplete-item
                                    :image-src="assetsData.item.imgSrc"
                                    :label="assetsData.item.filename"/>
                        </template>
                    </v-autocomplete>
                </div>


                <div class="display-assets">
                    <!-- show the assets -->
                    <div v-if="mixedAssetsAndUploads.length" class="premedia-items" ref="swapImages">
                        <premedia-item
                                v-for="(item, index) in mixedAssetsAndUploads"
                                cardtype="asset"
                                :key="generateUniqueHash(item)"
                                :id="item.id"
                                :temp-id="item.temp_id"
                                :row-id="id"
                                :row-index="rowIndex"
                                :title="item.title"
                                :begins="formatDate(parseItemMeta(item.metadata).beginDate)"
                                :expires="formatDate(parseItemMeta(item.metadata).expireDate)"
                                :type="item.type"
                                :item="item"
                                :note="item.note"
                                :approved="item.is_approved"
                                :img="item.url"
                                :isEditing="premediaRowIsEditing"
                                :can-approve="canApprove"
                                :can-view-approved="canViewApproved"
                        />
                        </div>
                    <div v-else><p>No assets attached</p></div>
                </div>
            </div>

            <div v-if="premediaRowDirections || premediaRowIsEditing" class="content__directions grid-f">
                <h3 class="section-title body-1"><span v-if="premediaRowIsEditing">Add </span>Directions</h3>
                <p v-if="!premediaRowIsEditing">{{ premediaRowDirections }}</p>
                <v-text-field
                        maxlength="500"
                        v-if="premediaRowIsEditing"
                        solo dense
                        v-model="premediaRowDirections"/>
            </div>

        </div>


        <!-- Swap Controls -->
        <div
                v-if="canEdit"
                class="premedia-group__controls grid-c">
            <div v-if="premediaRowIsEditing">
                <v-btn
                        small
                        color="primary"
                        @click="saveEdits(false)">
                    SAVE
                </v-btn>
                <v-btn
                        small
                        @click="cancelEdits">
                    CANCEL
                </v-btn>
                <v-btn
                        small flat depressed
                        class="label control__delete-row"
                        @click="deleteItem">Delete Row
                </v-btn>
            </div>
            <div v-if="!premediaRowIsEditing && !premediaGroupIsEditing">
                <v-btn
                        small
                        color="primary"
                        @click="startEditMode">
                    Edit
                </v-btn>
            </div>
        </div>

    </section>
</template>


<script>
    import dayjs from 'dayjs'
    import cloneDeep from 'lodash/cloneDeep'

    import {mapState, mapGetters, mapActions, mapMutations} from 'vuex'

    import {
        VUEX_PREMEDIA_EDIT_ROW,
        VUEX_PREMEDIA_SAVE_ROW,
        VUEX_PREMEDIA_CANCEL_EDIT,
        VUEX_PREMEDIA_DELETE_ROW,
        VUEX_PREMEDIA_GET_PRODUCTS,
        VUEX_PREMEDIA_ADD_PRODUCT,
        VUEX_PREMEDIA_GET_ASSETS,
        VUEX_PREMEDIA_ADD_ASSET,
        VUEX_PREMEDIA_CLEAR_PRODUCT_DATA,
        VUEX_PREMEDIA_CLEAR_ASSET_DATA,
        VUEX_PREMEDIA_REMOVE_FILE,
    } from '@/store/constants/premedia'

    import EditableTextArea from '@/components/ui/_global/editable/TextArea.vue'
    import PremediaItem from '@/components/ui/Jobs/Job_Premedia_PremediaGroup_Item.vue'
    import AutocompleteListItem from '@/components/ui/_global/Autocomplete_List_Item'
    import CoreUploader from '@/components/ui/_global/uploaders/Core_Uploader.vue'
    import AttachmentFileItem from '@/components/ui/_global/Attachment_File_Item.vue'
    import AddItemButton from '@/components/ui/_global/Add_Item_Button.vue'
    import PreviewableFileItem from '@/components/ui/_global/Previewable_File_Item.vue'

    export default {
        name: 'job-premedia-premediaGroup',

        components: {
            'editable-text-area': EditableTextArea,
            'premedia-item': PremediaItem,
            'autocomplete-item': AutocompleteListItem,

            'core-uploader': CoreUploader,
            'attachment-file-item': AttachmentFileItem,
            'add-item-button' : AddItemButton,
            'previewable-file-item': PreviewableFileItem,
        },

        props: {
            index: {
                type: [Number, String],
                default: '-'
            },
            id: {
                type: String,
                default: null
            },
            type: {
                type: Number,
                default: 1
            },
            shoes: {
                type: Array
            },
            images: {
                type: Array
            },
            value: {
                type: Object
            },

            canCreate: {
                type: Boolean,
                required: false,
                default: false
            },
            canEdit: {
                type: Boolean,
                required: false,
                default: false
            },
            canApprove: {
                type: Boolean,
                required: false,
                default: false
            },
            canViewApproved: {
                type: Boolean,
                required: false,
                default: true
            },

        },

        data: () => ({
            productsModel: null,
            assetsModel: null,
            hasTermData: false,
            hasItems: false,

            clickedUploadPremediaAttachment:false,

            options: {
                premedia_row_types: [
                    {
                        value: 1,
                        name: "Swap"
                    },
                    {
                        value: 2,
                        name: "Shot"
                    },
                    {
                        value: 3,
                        name: "Retouch"
                    }
                ],
                premedia_item_types: [
                    {
                        value: 1,
                        name: "Product"
                    },
                    {
                        value: 2,
                        name: "Asset"
                    },
                    {
                        value: 3,
                        name: "Upload"
                    }
                ],
            },
        }),

        computed: {
            ...mapState({
                premedia: state => state.premedia.premediaRows,
                premediaRowsFromJobs : state => state.jobs.job.premedia_rows,
                premediaGroupIsEditing: state => state.premedia.premediaGroupIsEditing,
                premediaRow_ItemsRemoved: state => state.premedia.premediaRow_ItemsRemoved,
                productsData: state => state.premedia.productsData,
                productsLoading: state => state.premedia.productsLoading,
                assetsData: state => state.premedia.assetsData,
                assetsLoading: state => state.premedia.assetsLoading,
                premedia_attachmentsRemoved: state => state.premedia.premedia_attachmentsRemoved,
            }),

            ...mapGetters({
                getRowProductItems:             'getRowProductItems',
                getRowAssetItems:               'getRowAssetItems',
                filterAttachmentsRemoved:       'filterAttachmentsRemoved',

                getQueuedFiles:                 'getQueuedFiles',
                getUploadingFiles  :            'getUploadingFiles',
                getProcessingFiles :            'getProcessingFiles',
                getAttachingFiles  :            'getAttachingFiles',
                getCompletedFiles  :            'getCompletedFiles',
            }),

            rowIndex() {
                return this.index
            },

            products() {
                var arr = this.getRowProductItems(this.index)
                return arr
            },
            assets() {
                var arr = this.getRowAssetItems(this.index)
                var ret = []
                for (var i=0;i<arr.length;i++) {
                    var obj = cloneDeep(arr[i])
                    obj.title = this.parseItemMeta(arr[i].metadata).filename
                    ret.push(obj)
                }

                //filter out removed stuff
                ret = this.filterAttachmentsRemoved(ret)

                return ret
            },


            mixedAssetsAndUploads() {
                //get assets
                var ret = this.assets

                //merge uploads into this after
                ret = ret.concat(this.premediaAttachments())

                //add index for the front end to reference
                for (var i = 0; i < ret.length; i++) {
                    ret[i].items_index = i
                }

                //filter out removed stuff
                ret = this.filterAttachmentsRemoved(ret)

                return ret
            },


            premediaRowIsEditing: {
                get() {
                    return this.premedia[this.index].isEditing
                },
                set(val) {
                    this.$store.dispatch('VUEX_PREMEDIA_UPDATE_ROW_PROP', this.index)
                }
            },
            premediaRowType: {
                get() {
                    return this.premedia[this.index].type
                },
                set(val) {
                    this.$store.dispatch('VUEX_PREMEDIA_UPDATE_ROW_PROP', {index: this.index, data: {type: val}})
                }
            },
            premediaRowDirections: {
                get() {
                    return this.premedia[this.index].directions
                },
                set(val) {
                    this.$store.dispatch('VUEX_PREMEDIA_UPDATE_ROW_PROP', {index: this.index, data: {directions: val}})
                }
            },

            premediaProducts: {
                get() {
                    return this.premedia[this.index].products
                },
                set(val) {
                }
            },
            productsTerm: {
                get() {
                    return this.$store.state.premedia.productsTerm
                },
                set(val) {
                    this.hasTermData = false

                    if (!val) return

                    if (val.length > 1) {
                        let data = val
                        this.hasTermData = (data || this.productsLoading) ? true : false
                        this.$store.dispatch('VUEX_PREMEDIA_UPDATE_PRODUCT_TERM', val)
                    }
                }
            },
            assetsTerm: {
                get() {
                    return this.$store.state.premedia.assetsTerm
                },
                set(val) {
                    this.hasTermData = false

                    if (!val) return

                    if (val.length > 1) {
                        let data = val
                        this.hasTermData = (data || this.assetsLoading) ? true : false
                        this.$store.dispatch('VUEX_PREMEDIA_UPDATE_ASSET_TERM', val)
                    }
                }
            },

            /* getType() {
                switch (this.type) {
                    case 'swaps' :
                        return 'shoe-swap'

                    default :
                        return 'retouch'
                }
            }, */

            parseItemMeta() {
                return (meta) => {
                    let ret = ''
                    if (meta !== undefined) {
                        if (typeof meta == 'object') {
                            ret = meta
                        } else {
                            ret = JSON.parse(meta)
                        }
                    }
                    return ret
                }
            },

            getRowTypeLabel() {
                let obj = this.options.premedia_row_types.filter(item => {
                    return item.value == this.premediaRowType
                })
                return obj[0].name
            },


            ////////////////////////////////////////////////////////////////////////////////////////
            //FOR ASSET UPLOADER
            fileUsageType() {
                return SILA_FILE_USAGE_TYPE_JOB_PREMEDIA_ATTACHMENT
            },
            attachTo() {
                return {
                    model: SILA_FILE_ATTACHMENT_MODEL_JOB_PREMEDIA_ROW,
                    model_id: this.id,
                }
            },

            ////////////////////////////////////////////////////////////////////////////////////////
        },

        mounted() {

        },

        methods: {
            ...mapActions({
                getProducts: VUEX_PREMEDIA_GET_PRODUCTS,
                getAssets: VUEX_PREMEDIA_GET_ASSETS,
                initPremediaEdit: VUEX_PREMEDIA_EDIT_ROW,
                addProduct: VUEX_PREMEDIA_ADD_PRODUCT,
                addAsset: VUEX_PREMEDIA_ADD_ASSET,
                saveRowEdit: VUEX_PREMEDIA_SAVE_ROW,
                cancelRowEdit: VUEX_PREMEDIA_CANCEL_EDIT,
            }),

            ...mapMutations({
                clearProductData: VUEX_PREMEDIA_CLEAR_PRODUCT_DATA,
                clearAssetData: VUEX_PREMEDIA_CLEAR_ASSET_DATA
            }),

            formatDate(date) {
                date = String(date).substring(0,10) //IGNORE TIMEZONE!!!
                return date ? dayjs(date).format('MM/DD/YY') : null
            },

            startEditMode() {
                this.initPremediaEdit(this.index)
            },
            endEditMode() {
                this.initPremediaEdit()
            },

            saveEdits(uploadSneakSave) {
                let obj = {
                    rowId:this.id,
                    uploadSneakSave:uploadSneakSave,
                }
                this.saveRowEdit(obj)
            },

            cancelEdits() {
                this.cancelRowEdit()
            },

            deleteItem() {
                //GLOBAL ACTION PROMPT: Pass messages & dispatch action
                let actionObj = {
                    checkChangedFields: false,
                    title: 'ARE YOU SURE?',
                    description: 'All data will be removed and can not be recovered.',
                    dispatchAction: VUEX_PREMEDIA_DELETE_ROW,
                    dispatchActionObject: {}
                }
                this.$store.dispatch('VUEX_GLOBAL_ACTION_PROMPT_SHOW', {actionObj: actionObj})
            },


            ////////////////////////////////////////////////////////////////////////////////////////
            //FOR ASSET UPLOADER
            uploadPremediaAttachment: async function() {
                //save fist so we can have an id
                this.clickedUploadPremediaAttachment = true
                if (this.id === undefined || this.id === null) {
                    await this.saveEdits(true)
                }
                this.uploadPremediaAttachment2()
            },
            uploadPremediaAttachment2: function() {
                if (this.clickedUploadPremediaAttachment) {
                    if (this.$refs?.coreUploader) {
                        this.$refs?.coreUploader?.init()
                        this.clickedUploadPremediaAttachment = false
                    }
                }
            },

            premediaAttachments() {
                var ret = []

                ///////////////////////////////////////////////////////////////
                //PART 1
                //get assets based on jobs premedia rows column
                //use this.id to find the premedia_items instead of this.index, because inserting new rows gets screwed up with index
                ///////////////////////////////////////////////////////////////
                if (this.id !== null && this.premediaRowsFromJobs ) {
                    for (let i = 0; i < this.premediaRowsFromJobs.length; i++) {
                        if (this.premediaRowsFromJobs[i].id === this.id ) {
                            var obj = this.premediaRowsFromJobs[i]
                            if (obj.items && obj.items.length > 0) {

                                //we got the premedia items, now loop through them and pluck the type 3s
                                //also don't add anything from the front end que, as that gets sliced in there
                                for (let ii = 0; ii < obj.items.length; ii++) {
                                    if (obj.items[ii].type === 3) {
                                        //this is an uploaded file
                                        //so we need to grab values differently
                                        var obj2 = cloneDeep(obj.items[ii]);
                                        var file = null
                                        var title = null
                                        if (obj2.file !== null && obj2.file !== undefined) {
                                            //check for children previews
                                            if (obj2.file.children !== undefined) {
                                                for (var n = 0; n < obj2.file.children.length; n++) {
                                                    if (obj2.file.children[n].usage_type === SILA_FILE_USAGE_TYPE_JOB_PREMEDIA_ATTACHMENT_PREVIEW) {
                                                        if (obj2.file.children[n] !== null) {
                                                            file = obj2.file.children[n].uri
                                                        }
                                                    }
                                                }
                                            }
                                            //if no children, load parent
                                            //also load title here
                                            if (file === null || file === undefined) {
                                                if (obj2.file.uri !== null) {
                                                    file = obj2.file.uri
                                                }
                                                if (obj2.file.filename !== null) {
                                                    title = obj2.file.filename
                                                }
                                            }
                                        }
                                        obj2.img = file
                                        obj2.title = title
                                        ret.push(obj2)

                                    } //if type is 3

                                    //set title

                                } //for ii


                            }//if items
                        }//if found id
                    }//for i
                }//if not null

                ///////////////////////////////////////////////////////////////
                //PART 2
                //now that we got json, add in the loader files, which will appear temporarily before the save
                ///////////////////////////////////////////////////////////////

                let paramsWithId = {
                    attach_to: {
                        model: SILA_FILE_ATTACHMENT_MODEL_JOB_PREMEDIA_ROW,
                        model_id: this.id,
                    }
                }

                let loaderfiles = []
                loaderfiles = loaderfiles
                    .concat(this.getCompletedFiles(paramsWithId))
                    //.concat(this.getUploadingFiles(paramsWithId))
                    //.concat(this.getProcessingFiles(paramsWithId))
                    //.concat(this.getAttachingFiles(paramsWithId))
                    //.concat(this.getQueuedFiles(paramsWithId))

                //only display loader files if they are not already in our assets set (avoids things appearing twice)
                if (loaderfiles.length > 0) {
                    for (var i = 0; i < loaderfiles.length; i++) {
                        var addIt = true
                        for (var ii = 0; ii < ret.length; ii++) {
                            if (loaderfiles[i].id === ret[ii].file_id) {
                                addIt = false
                            }
                        }//ii
                        if (addIt) {
                            var obj = cloneDeep(loaderfiles[i])
                            obj.preview = null
                            if (loaderfiles[i].preview !== undefined && loaderfiles[i].preview !== null) {
                                obj.preview = loaderfiles[i].preview
                            } else if (loaderfiles[i].uri !== undefined && loaderfiles[i].uri !== null) {
                                obj.preview = loaderfiles[i].uri
                            }

                            //so passing "file" to the component yields Error in render: "Error: Unknown object type "file""
                            //i'm deleting this key and manually making what i need
                            let obj2 = cloneDeep(obj)
                            obj2.file = {}
                            obj2.note = ''
                            obj2.type = 3
                            obj2.url = ''
                            obj2.metadata = {}
                            obj2.title = (obj.file.name) ? obj.file.name : obj.filename
                            obj2.file.filename = obj2.title
                            obj2.file.uri = obj.file.uri
                            obj2.file.mimetype = obj2.file.mimetype
                            obj2.file.id = obj2.file.id
                            obj2.img = obj2.preview
                            ret.push(obj2)
                        }
                    }//i


                }

                return ret
            },
            ////////////////////////////////////////////////////////////////////////////////////////

        },

        watch: {
            products(val) {
                this.hasItems = (val > 0 || this.products.length > 0) ? true : false
            },
            assets(val) {
                this.hasItems = (val > 0 || this.assets.length > 0) ? true : false
            },

            productsModel(val) {
                this.clearProductData()
                if (!val) return

                // PASS OBJECT TO VUEX
                this.addProduct({
                    type: 1,
                    metadata: val,
                    note: null,
                    approved: false,
                })

                this.$nextTick(() => {
                    this.productsModel = null
                })
            },

            assetsModel(val) {
                this.clearAssetData()
                if (!val) return

                // PASS OBJECT TO VUEX
                this.addAsset({
                    type: 2,
                    metadata: val,
                    note: null,
                    approved: false,
                })

                this.$nextTick(() => {
                    this.assetsModel = null
                })
            },

            'id': function (newValue) {
                //premedia upload watches this
                if (newValue !== null ) {
                    // two next ticks
                    // the id has changed, and now this function will call the core uploader init
                    // but it needs two ticks: one for the id change, and one for the component load
                    const t = this
                    setTimeout(function() {
                        this.uploadPremediaAttachment2()
                    }.bind(t),100)
                }
            }
        }
    }
</script>